import type { ConviteLinha } from '@triade/contracts';
import { Button } from '@/components/ui/button';
import { formatarDataHora } from '@/lib/format';
import { SituacaoConviteBadge } from './situacao-badge';
import { rotuloAcesso } from './tipos';

export interface ConvitesTabelaProps {
  convites: ConviteLinha[];
  ocupadoId: string | null;
  onReenviar: (c: ConviteLinha) => void;
  onRevogar: (c: ConviteLinha) => void;
}

/** Rastro do e-mail do convite (Sessao 48): so apresentacao do que a API devolveu. Convite antigo (emailStatus nulo) = travessao. */
export function TextoEnvioEmail({ c }: { c: ConviteLinha }) {
  if (c.emailStatus === 'ENVIADO') return <span>Enviado em {formatarDataHora(c.emailEnviadoEm)}</span>;
  if (c.emailStatus === 'FALHOU') {
    return (
      <span data-testid="email-convite-falhou" title={c.emailErro ?? undefined} className="block max-w-[220px] truncate text-danger-ink">
        Falhou: {c.emailMotivo ?? 'motivo não informado'}
      </span>
    );
  }
  if (c.emailStatus === 'NAO_CONFIGURADO') return <span className="text-ink-muted">Não configurado</span>;
  return <span className="text-ink-muted">—</span>;
}

/** Grade de convites: situacao, envio do e-mail, validade e as acoes de reenviar e revogar (so para quem pode, pelo teto). */
export function ConvitesTabela({ convites, ocupadoId, onReenviar, onRevogar }: ConvitesTabelaProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] border-collapse text-small">
        <thead>
          <tr className="text-left text-caption text-ink-muted">
            <th scope="col" className="px-4 py-2.5 font-medium">E-mail</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Acessos</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Situação</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Envio do e-mail</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Vale até</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Convidado por</th>
            <th scope="col" className="px-4 py-2.5 text-right font-medium">Ações</th>
          </tr>
        </thead>
        <tbody>
          {convites.map((c) => {
            const reenviavel = c.situacao === 'PENDENTE' || c.situacao === 'EXPIRADO';
            return (
              <tr key={c.id} data-testid="linha-convite" className="border-t border-line-soft">
                <td className="px-4 py-3">
                  <div className="font-medium">{c.email}</div>
                  {c.nome && <div className="text-caption text-ink-muted">{c.nome}</div>}
                </td>
                <td className="px-3 py-3">
                  <ul className="flex flex-wrap gap-1.5">
                    {c.acessos.map((a, i) => (
                      <li key={i} className="rounded-full bg-line-soft px-2 py-0.5 text-caption text-ink-body">
                        {rotuloAcesso(a)}
                      </li>
                    ))}
                  </ul>
                </td>
                <td className="px-3 py-3">
                  <SituacaoConviteBadge situacao={c.situacao} />
                </td>
                <td className="px-3 py-3 text-caption text-ink-secondary">
                  <TextoEnvioEmail c={c} />
                </td>
                <td className="tabular px-3 py-3 text-ink-secondary">{formatarDataHora(c.expiraEm)}</td>
                <td className="px-3 py-3 text-ink-secondary">{c.criadoPorNome ?? '—'}</td>
                <td className="px-4 py-3 text-right">
                  {c.podeRevogar && reenviavel ? (
                    <div className="inline-flex gap-2">
                      <Button type="button" variant="outline" size="sm" disabled={ocupadoId === c.id} onClick={() => onReenviar(c)} aria-label={`Reenviar convite para ${c.email}`}>
                        Reenviar
                      </Button>
                      {c.situacao === 'PENDENTE' && (
                        <Button type="button" variant="ghost" size="sm" disabled={ocupadoId === c.id} onClick={() => onRevogar(c)} aria-label={`Revogar convite para ${c.email}`} className="text-danger-ink">
                          Revogar
                        </Button>
                      )}
                    </div>
                  ) : (
                    <span className="text-caption text-ink-muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
